import { Calendar03Icon, Folder01Icon, Message01Icon } from "@hugeicons/core-free-icons";
import { GlowCard } from "@/ui/components/glow-card";
import { RouteBadge } from "@/components/route-badge";
import { DenseRow, MutedChip, RowBubble, RowCard, RowHit, RowTitle } from "@/components/kit/rows";
import { TabEmpty } from "@/components/kit/tab-empty";
import type { MetricRow, ProjectRow, Session } from "@/lib/api";
import { formatCost, formatNumber, timeAgo } from "@/lib/utils";

export function ProjectsView({ rows, onSelect }: { rows: ProjectRow[]; onSelect: (project: string) => void }) {
  if (!rows.length) return <TabEmpty title="No projects yet" icon={Folder01Icon} />;
  const top = Math.max(...rows.map((r) => r.cost), 0.0001);
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row) => {
        const share = (row.cost / top) * 100;
        return (
          <RowCard key={row.project}>
            <RowHit label={`Open ${row.project}`} onClick={() => onSelect(row.project)} />
            <RowBubble icon={Folder01Icon} small />
            <RowTitle
              title={row.project === "unassigned" ? <span className="text-muted-foreground">unassigned</span> : row.project}
              sub={`${row.sessions} ${row.sessions === 1 ? "session" : "sessions"} · ${timeAgo(row.last_active)}`}
            />
            <div className="pointer-events-none col-span-3 flex min-w-0 flex-wrap items-center gap-2 md:col-span-1 md:justify-center">
              <MutedChip>{formatNumber(row.tokens)} tokens</MutedChip>
              <MutedChip>{formatCost(row.cost)}</MutedChip>
            </div>
            <div
              className="pointer-events-none col-span-3 flex w-full items-center gap-2 md:col-span-1 md:w-36"
              title="Share of cost"
            >
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-foreground/[0.07]">
                <div className="h-full rounded-full bg-chart-2" style={{ width: `${share}%` }} />
              </div>
              <span className="w-9 text-end text-xs text-muted-foreground tabular-nums">{Math.round(share)}%</span>
            </div>
          </RowCard>
        );
      })}
    </div>
  );
}

export function SessionsView({ rows, showProject }: { rows: Session[]; showProject: boolean }) {
  if (!rows.length) return <TabEmpty title="No sessions yet" icon={Message01Icon} />;
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row) => (
        <RowCard key={`${row.session_id}-${row.model}`}>
          <RowBubble icon={Message01Icon} small />
          <RowTitle
            title={<span className="font-mono text-[15px]">{row.session_id?.slice(0, 8) ?? "—"}</span>}
            sub={showProject ? (row.project ?? "—") : undefined}
          />
          <div className="pointer-events-none col-span-3 flex min-w-0 flex-wrap items-center gap-2 md:col-span-1 md:justify-center">
            <MutedChip>{row.model ?? "—"}</MutedChip>
            <MutedChip>{formatNumber(row.tokens)} tokens</MutedChip>
            <MutedChip>{formatCost(row.cost)}</MutedChip>
          </div>
          <span className="pointer-events-none text-end text-[13px] text-muted-foreground max-md:col-start-3 max-md:row-start-1">
            {timeAgo(row.last_active)}
          </span>
        </RowCard>
      ))}
    </div>
  );
}

export function RecentView({ rows }: { rows: MetricRow[] }) {
  if (!rows.length) return <TabEmpty title="No telemetry yet" icon={Calendar03Icon} />;
  return (
    <GlowCard className="p-2">
      <ul className="grid gap-0.5">
        {rows.map((row) => {
          const labels = JSON.parse(row.labels) as Record<string, unknown>;
          return (
            <DenseRow key={row.id}>
              <span className="w-16 shrink-0 text-xs text-muted-foreground max-md:w-12">{timeAgo(row.ts)}</span>
              <span className="min-w-0 flex-1 truncate font-mono">{row.name.replace("claude_code.", "")}</span>
              {labels.type ? (
                <span className="shrink-0 rounded-full bg-foreground/[0.04] px-2.5 py-0.5 text-xs text-muted-foreground max-md:hidden">
                  {String(labels.type)}
                </span>
              ) : null}
              <span className="w-16 shrink-0 text-end tabular-nums">{formatNumber(row.value)}</span>
              <span className="w-24 shrink-0 text-end max-md:w-16">
                <RouteBadge route={row.route} />
              </span>
            </DenseRow>
          );
        })}
      </ul>
    </GlowCard>
  );
}
